import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bookmark, Eye, FolderOpen, Bell, Lock, Trash2, Share2 } from "lucide-react";
import { api, errMsg, fmtDate, titleUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import { MovieCard } from "@/components/MovieCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, Empty, Loading, ErrorBox, Spoiler } from "@/components/Common";
import { Stars } from "@/components/StarInput";
import { ReportButton } from "@/components/ReportButton";
import { PostCard } from "@/pages/Community";

const inp = "w-full rounded-xl border border-white/10 bg-black/30 px-4 py-2.5 text-sm outline-none focus:border-amber-400";
const Grid = ({ items }) => <div className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">{items.map((t) => <MovieCard key={t.id} t={t} size="fluid" />)}</div>;

function SavedList({ kind, empty }) {
  const [sort, setSort] = useState("added");
  const [genre, setGenre] = useState("");
  const [items, setItems] = useState(null);
  useEffect(() => { api.get(`/me/${kind}`, { params: { sort } }).then((r) => setItems(r.data.items)).catch(() => setItems([])); }, [kind, sort]);
  if (!items) return <Loading />;
  const genres = [...new Set(items.flatMap((t) => t.genres || []))].sort();
  const shown = genre ? items.filter((t) => t.genres?.includes(genre)) : items;
  return (
    <>
      <div className="mb-5 flex flex-wrap gap-2">
        <select data-testid={`${kind}-sort`} value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-full border border-white/10 bg-[#12121A] px-3 py-1.5 text-xs">
          <option value="added">Recently added</option><option value="title">Title A–Z</option><option value="year">Release year</option><option value="rating">Community rating</option>
        </select>
        <select data-testid={`${kind}-genre`} value={genre} onChange={(e) => setGenre(e.target.value)} className="rounded-full border border-white/10 bg-[#12121A] px-3 py-1.5 text-xs"><option value="">All genres</option>{genres.map((g) => <option key={g}>{g}</option>)}</select>
      </div>
      {shown.length ? <Grid items={shown} /> : empty}
    </>
  );
}

function Collections() {
  const [items, setItems] = useState(null);
  const [f, setF] = useState({ name: "", description: "", public: true });
  const load = () => api.get("/me/collections").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/me/collections", f); setF({ name: "", description: "", public: true }); load(); toast.success("Collection created"); } catch (err) { toast.error(errMsg(err)); }
  };
  if (!items) return <Loading />;
  return (
    <div className="grid gap-8 lg:grid-cols-[320px_1fr]">
      <form onSubmit={create} className="h-fit space-y-3 rounded-2xl border border-white/5 bg-[#12121A] p-5" data-testid="collection-form">
        <p className="font-display text-2xl">New collection</p>
        <input data-testid="collection-name" required minLength={2} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Movies to Watch This Weekend" className={inp} />
        <textarea data-testid="collection-desc" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Description" className={`${inp} min-h-[70px]`} />
        <label className="flex items-center gap-2 text-sm text-slate-400"><input type="checkbox" checked={f.public} onChange={(e) => setF({ ...f, public: e.target.checked })} className="accent-amber-400" />Public</label>
        <button data-testid="collection-create" className="w-full rounded-full bg-purple-600 py-2.5 text-sm font-bold">Create</button>
      </form>
      <div className="grid gap-4 sm:grid-cols-2">
        {items.length === 0 && <Empty icon={FolderOpen} title="No collections yet" text="Group titles into lists like “Underrated Sci-Fi” and share them." />}
        {items.map((c) => (
          <Link key={c.id} to={`/collection/${c.id}`} data-testid={`collection-${c.id}`} className="rounded-2xl border border-white/5 bg-[#12121A] p-5 hover:border-purple-400/30">
            <p className="flex items-center gap-2 font-semibold">{c.name}{!c.public && <Lock className="h-3.5 w-3.5 text-slate-500" />}</p>
            <p className="mt-1 line-clamp-2 text-xs text-slate-400">{c.description}</p>
            <p className="mt-3 text-xs text-amber-400">{c.count} titles</p>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function Watchlist() {
  useSeo({ title: "My Watchlist", noindex: true });
  return (
    <div className="px-4 py-10 sm:px-6 lg:px-12" data-testid="watchlist-page">
      <h1 className="font-display text-6xl">My Library</h1>
      <Tabs defaultValue="watchlist" className="mt-6">
        <TabsList className="bg-white/5">
          <TabsTrigger value="watchlist" data-testid="tab-watchlist">Watchlist</TabsTrigger>
          <TabsTrigger value="watched" data-testid="tab-watched">Watched</TabsTrigger>
          <TabsTrigger value="collections" data-testid="tab-collections">Collections</TabsTrigger>
        </TabsList>
        <TabsContent value="watchlist" className="mt-6"><SavedList kind="watchlist" empty={<Empty icon={Bookmark} title="Your watchlist is empty" text="Tap “Add to Watchlist” on any title to save it here — synced across all your devices." action={<Link to="/movies" className="rounded-full bg-amber-400 px-5 py-2.5 text-sm font-bold text-black">Browse movies</Link>} testid="watchlist-empty" />} /></TabsContent>
        <TabsContent value="watched" className="mt-6"><SavedList kind="watched" empty={<Empty icon={Eye} title="Nothing marked as watched" text="Mark titles as watched to build your history and get better recommendations." />} /></TabsContent>
        <TabsContent value="collections" className="mt-6"><Collections /></TabsContent>
      </Tabs>
    </div>
  );
}

export function CollectionPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [err, setErr] = useState(false);
  const load = useCallback(() => api.get(`/collections/${id}`).then((r) => setC(r.data)).catch(() => setErr(true)), [id]);
  useEffect(() => { load(); }, [load]);
  useSeo({ title: c ? `${c.name} — collection by @${c.user.username}` : "Collection", description: c?.description, noindex: c && !c.public });
  if (err) return <ErrorBox text="This collection is private or doesn't exist." />;
  if (!c) return <Loading />;
  const mine = user && user.id === c.user_id;
  const remove = async (tid) => { await api.post(`/me/collections/${c.id}/titles/${tid}`); load(); };
  const del = async () => { if (!window.confirm("Delete this collection?")) return; await api.delete(`/me/collections/${c.id}`); nav("/watchlist"); };
  const togglePublic = async () => { await api.put(`/me/collections/${c.id}`, { name: c.name, description: c.description, public: !c.public }); load(); };
  return (
    <div className="px-4 py-10 sm:px-6 lg:px-12" data-testid="collection-page">
      <p className="eyebrow">Collection · by <Link to={`/u/${c.user.username}`}>@{c.user.username}</Link></p>
      <h1 className="font-display text-6xl">{c.name}</h1>
      {c.description && <p className="mt-2 max-w-2xl text-slate-400">{c.description}</p>}
      <div className="mt-4 flex flex-wrap gap-3 text-sm">
        {c.public && <button onClick={() => { navigator.clipboard?.writeText(window.location.href); toast.success("Link copied"); }} data-testid="share-collection" className="flex items-center gap-1 text-amber-400"><Share2 className="h-4 w-4" />Share</button>}
        {mine && <><button onClick={togglePublic} data-testid="toggle-collection-public" className="text-slate-300">{c.public ? "Make private" : "Make public"}</button><button onClick={del} data-testid="delete-collection" className="text-red-400">Delete</button></>}
        {!mine && <ReportButton type="collection" id={c.id} />}
      </div>
      <div className="mt-8">
        {c.items.length ? (
          <div className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {c.items.map((t) => <div key={t.id}><MovieCard t={t} size="fluid" />{mine && <button onClick={() => remove(t.id)} data-testid={`remove-from-collection-${t.slug}`} className="mt-1 text-xs text-red-400">Remove</button>}</div>)}
          </div>
        ) : <Empty title="Empty collection" text="Add titles from any movie page using the Collection button." />}
      </div>
    </div>
  );
}

export function Profile() {
  const { username } = useParams();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const load = useCallback(() => api.get(`/users/${username}`).then((r) => setD(r.data)).catch(() => setErr(true)), [username]);
  useEffect(() => { load(); }, [load]);
  useSeo({ title: d ? `${d.user.name} (@${d.user.username})` : "Profile", noindex: d?.hidden });
  if (err) return <ErrorBox text="Member not found." />;
  if (!d) return <Loading />;
  const act = async (kind) => { try { await api.post(`/users/${d.user.id}/${kind}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="px-4 py-10 sm:px-6 lg:px-12" data-testid="profile-page">
      <div className="flex flex-wrap items-center gap-5">
        <Avatar user={d.user} size={88} />
        <div className="flex-1">
          <h1 className="font-display text-5xl">{d.user.name}</h1>
          <p className="text-sm text-slate-400">@{d.user.username} {d.user.role === "admin" && <span className="ml-1 rounded bg-amber-400 px-1.5 text-[10px] font-bold text-black">CREATOR</span>} · joined {fmtDate(d.user.created_at)}</p>
          {d.user.bio && <p className="mt-2 max-w-xl text-sm text-slate-300">{d.user.bio}</p>}
          <p className="mt-2 text-sm"><b>{d.followers}</b> <span className="text-slate-500">followers</span> · <b>{d.following}</b> <span className="text-slate-500">following</span></p>
        </div>
        {d.is_me ? <Link to="/settings" data-testid="edit-profile" className="rounded-full border border-white/15 px-5 py-2 text-sm">Edit profile</Link> : user && (
          <div className="flex items-center gap-3">
            <button data-testid="follow-btn" onClick={() => act("follow")} className={`rounded-full px-5 py-2 text-sm font-bold ${d.is_following ? "border border-white/15" : "bg-amber-400 text-black"}`}>{d.is_following ? "Following" : "Follow"}</button>
            <button data-testid="block-btn" onClick={() => act("block")} className="text-xs text-slate-500 hover:text-red-400">{d.is_blocked ? "Unblock" : "Block"}</button>
            <ReportButton type="profile" id={d.user.id} />
          </div>
        )}
      </div>
      {d.hidden ? <div className="mt-10"><Empty icon={Lock} title="This profile is private" /></div> : (
        <Tabs defaultValue="reviews" className="mt-10">
          <TabsList className="flex-wrap bg-white/5">
            {["reviews", "ratings", "watchlist", "watched", "collections", "posts"].map((k) => <TabsTrigger key={k} value={k} className="capitalize" data-testid={`profile-tab-${k}`}>{k}</TabsTrigger>)}
          </TabsList>
          <TabsContent value="reviews" className="mt-6 space-y-4">
            {!d.reviews.length && <p className="text-sm text-slate-500">No reviews yet.</p>}
            {d.reviews.map((r) => <div key={r.id} className="rounded-2xl border border-white/5 bg-[#12121A] p-5"><Link to={r.movie ? titleUrl(r.movie) : "#"} className="font-semibold text-amber-400">{r.movie?.title}</Link><Spoiler spoiler={r.spoiler} id={r.id}><p className="mt-2 text-sm text-slate-300">{r.body}</p></Spoiler></div>)}
          </TabsContent>
          <TabsContent value="ratings" className="mt-6 grid gap-3 sm:grid-cols-2">
            {!d.ratings.length && <p className="text-sm text-slate-500">No ratings yet.</p>}
            {d.ratings.map((r) => r.movie && <Link key={r.id} to={titleUrl(r.movie)} className="flex items-center justify-between rounded-xl bg-[#12121A] p-3 text-sm"><span>{r.movie.title}</span><Stars value={r.value} size={14} /></Link>)}
          </TabsContent>
          <TabsContent value="watchlist" className="mt-6">{d.watchlist.length ? <Grid items={d.watchlist} /> : <p className="text-sm text-slate-500">Empty.</p>}</TabsContent>
          <TabsContent value="watched" className="mt-6">{d.watched.length ? <Grid items={d.watched} /> : <p className="text-sm text-slate-500">Empty.</p>}</TabsContent>
          <TabsContent value="collections" className="mt-6 grid gap-3 sm:grid-cols-3">
            {!d.collections.length && <p className="text-sm text-slate-500">No public collections.</p>}
            {d.collections.map((c) => <Link key={c.id} to={`/collection/${c.id}`} className="rounded-xl bg-[#12121A] p-4"><p className="font-semibold">{c.name}</p><p className="text-xs text-amber-400">{c.count} titles</p></Link>)}
          </TabsContent>
          <TabsContent value="posts" className="mt-6 space-y-3">
            {!d.posts.length && <p className="text-sm text-slate-500">No posts yet.</p>}
            {d.posts.map((p) => <PostCard key={p.id} p={{ ...p, user: d.user }} />)}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

export function Settings() {
  const { user, setUser, logout } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ name: user.name, bio: user.bio || "", avatar: user.avatar || "", private: !!user.private });
  const [reason, setReason] = useState("");
  useSeo({ title: "Account Settings", noindex: true });
  const save = async (e) => {
    e.preventDefault();
    try { const { data } = await api.put("/me", f); setUser(data); toast.success("Profile saved"); } catch (err) { toast.error(errMsg(err)); }
  };
  const del = async () => {
    if (!window.confirm("Request permanent deletion of your account?")) return;
    try { const { data } = await api.post("/me/delete-request", { reason }); toast.success(data.message); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="mx-auto max-w-2xl px-4 py-10" data-testid="settings-page">
      <h1 className="font-display text-6xl">Account Settings</h1>
      <form onSubmit={save} className="mt-6 space-y-4 rounded-2xl border border-white/5 bg-[#12121A] p-6">
        <p className="text-sm text-slate-400">Signed in as <b>{user.email}</b> · @{user.username}</p>
        <label className="block text-xs text-slate-400">Display name<input data-testid="settings-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={`${inp} mt-1`} /></label>
        <label className="block text-xs text-slate-400">Short bio<textarea data-testid="settings-bio" maxLength={300} value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} className={`${inp} mt-1 min-h-[80px]`} /></label>
        <label className="block text-xs text-slate-400">Avatar image URL (https)<input data-testid="settings-avatar" value={f.avatar} onChange={(e) => setF({ ...f, avatar: e.target.value })} className={`${inp} mt-1`} /></label>
        <label className="flex items-center gap-2 text-sm text-slate-300"><input data-testid="settings-private" type="checkbox" checked={f.private} onChange={(e) => setF({ ...f, private: e.target.checked })} className="accent-amber-400" />Private profile (hide my activity from others)</label>
        <button data-testid="settings-save" className="rounded-full bg-amber-400 px-6 py-2.5 text-sm font-bold text-black">Save changes</button>
      </form>
      <div className="mt-6 flex gap-3"><Link to="/forgot-password" className="text-sm text-amber-400">Change password</Link><button data-testid="settings-signout" onClick={async () => { await logout(); nav("/"); }} className="text-sm text-slate-300">Sign out</button></div>
      <div className="mt-10 rounded-2xl border border-red-500/20 p-6">
        <p className="flex items-center gap-2 font-semibold text-red-300"><Trash2 className="h-4 w-4" />Delete my account</p>
        <p className="mt-1 text-xs text-slate-400">Your request is reviewed by an administrator and completed within 30 days. <Link to="/page/account-deletion" className="text-amber-400">What gets deleted?</Link></p>
        <textarea data-testid="delete-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" className={`${inp} mt-3`} />
        <button data-testid="delete-request" onClick={del} className="mt-3 rounded-full border border-red-500/40 px-5 py-2 text-sm text-red-300">Request deletion</button>
      </div>
    </div>
  );
}

export function Notifications() {
  const [items, setItems] = useState(null);
  useSeo({ title: "Notifications", noindex: true });
  useEffect(() => { api.get("/me/notifications").then((r) => { setItems(r.data.items); api.post("/me/notifications/read"); }); }, []);
  if (!items) return <Loading />;
  return (
    <div className="mx-auto max-w-2xl px-4 py-10" data-testid="notifications-page">
      <h1 className="font-display text-6xl">Notifications</h1>
      <div className="mt-6 space-y-2">
        {!items.length && <Empty icon={Bell} title="All quiet" text="Replies, follows and likes will show up here." />}
        {items.map((n) => (
          <Link key={n.id} to={n.link || "#"} className={`block rounded-xl border p-4 text-sm ${n.read ? "border-white/5 bg-[#12121A]" : "border-purple-400/30 bg-purple-500/10"}`}>
            {n.text}<p className="mt-1 text-xs text-slate-500">{fmtDate(n.created_at)}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function MyReviews() {
  const [d, setD] = useState(null);
  useSeo({ title: "My Reviews", noindex: true });
  const load = () => api.get("/me/reviews").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  if (!d) return <Loading />;
  const del = async (id) => { if (!window.confirm("Delete this review?")) return; await api.delete(`/reviews/${id}`); load(); };
  return (
    <div className="mx-auto max-w-3xl px-4 py-10" data-testid="my-reviews-page">
      <h1 className="font-display text-6xl">My Reviews & Ratings</h1>
      <h2 className="mt-8 font-display text-3xl">Reviews ({d.reviews.length})</h2>
      <div className="mt-3 space-y-3">
        {!d.reviews.length && <p className="text-sm text-slate-500">You haven't written any reviews yet.</p>}
        {d.reviews.map((r) => (
          <div key={r.id} className="rounded-2xl border border-white/5 bg-[#12121A] p-5">
            <div className="flex justify-between"><Link to={r.title ? titleUrl(r.title) : "#"} className="font-semibold text-amber-400">{r.title?.title}</Link>{r.status === "removed" ? <span className="text-xs text-red-400">Removed by moderator</span> : <button onClick={() => del(r.id)} className="text-xs text-red-400">Delete</button>}</div>
            <p className="mt-2 text-sm text-slate-300">{r.body}</p>
          </div>
        ))}
      </div>
      <h2 className="mt-10 font-display text-3xl">Ratings ({d.ratings.length})</h2>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {d.ratings.map((r) => r.title && <Link key={r.id} to={titleUrl(r.title)} className="flex items-center justify-between rounded-xl bg-[#12121A] p-3 text-sm"><span>{r.title.title}</span><Stars value={r.value} size={14} /></Link>)}
      </div>
    </div>
  );
}
